import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {formatDate} from "../../time";

import classes from "./PlaythroughsWidget.module.css";
import {Account, Contract} from "../../schema";
import {gql} from "@apollo/client";
import {analyticsSendEvent} from "../../libs/analytics";
import {useModal} from "../ModalProvider";
import PurchaseModal from "./PurchaseModal";
import Button from "../../ui/button";


type props = {
  account: Account
  contracts: Contract[] | undefined
}

export default function PlaythroughsWidget({
  account, contracts
}: props) {
  const {t, i18n} = useTranslation();

  const {add: addModal} = useModal();
  const onBuyMoreClick = useCallback(() => {
    analyticsSendEvent("openPurchaseModal", {
      from: "HomeScene"
    });

    let modal = addModal({
      header: <PurchaseModal.Header/>,
      content: (
        <PurchaseModal.Content
          onClose={() => modal.remove()}
        />
      )
    })
  }, [addModal]);

  const contractsSummary = useMemo(() => {
    if (!contracts || contracts.length < 1) {
      return undefined
    }

    let licensesCount = 0
    let finalDate: number = contracts[0].expireAt

    contracts.forEach(item => {
      if (item.isActive) {
        licensesCount += item.licensesCount;
        if (item.expireAt > finalDate) {
          finalDate = item.expireAt;
        }
      }
    })

    return {
      count: licensesCount,
      expireAt: formatDate(finalDate, i18n.language)
    }
  }, [i18n.language, contracts])


  const widget = useMemo(() => {
    return (
      <div>
        {contractsSummary ? (
          <div className={classes.info}>
            <span>{t("components.Playthroughs.licensesCount", {count: contractsSummary.count})}</span>
            <span>
              {t("components.Playthroughs.expireAt")} {contractsSummary.expireAt}
            </span>
          </div>
        ) : (
          <div className={classes.info}>
            <span>{t("components.Playthroughs.left", {count: account.attemptsRemains ?? 0})}</span>
          </div>
        )}

        <Button className={classes.buttonAlt} color="primary" onClick={onBuyMoreClick}>
          {t("components.Playthroughs.buyMoreLicenses")}
        </Button>
      </div>
    );
  }, [t, account.attemptsRemains, contractsSummary, onBuyMoreClick]);

  return (
    <div className={classes.root}>
      {widget}
    </div>
  )
}

PlaythroughsWidget.fragments = {
  account: gql`
    fragment PlaythroughsWidgetAccount on Account {
      id
      attemptsRemains
    }
  `,
}
